import { useEffect, useState } from 'react';

import {
    Flex,
    Icon,
    Menu,
    MenuButton,    
    MenuItem,
    MenuList,
    Spacer,
    Tab,
    TabList,    
    Tabs,
    useBreakpointValue,
    useTheme
} from '@chakra-ui/react';

import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { RxHamburgerMenu } from "react-icons/rx";
import { FaRegFileAlt, FaRegCalendarMinus, FaUserEdit, FaMusic } from "react-icons/fa";
import { FaListOl, FaRegCalendar, FaRegCalendarPlus } from "react-icons/fa6";
import { TbLogin, TbShirt, TbLocation, TbLogout } from "react-icons/tb";

export function Navigation({
    requestActive, 
    sessionActive, 
    currentPage, 
    regAllowed, 
    schedAllowed, 
    shirtsAllowed, 
    shirtsOver, 
    clearAllowed, 
    rankingAllowed, 
    songdataAllowed, 
    eventOver, 
    pageOnClick, 
    clearSetRequest, 
    logoutRequest
}) {

    const theme = useTheme();

    const isMobile = useBreakpointValue(
        {
          "base": ()=> true,
          "xl": ()=>  false
        }
    );

    const navItems = [
        {
            isDefaultTab: false,
            short: "log-in",
            long: "Log In",
            icon: <Icon as={TbLogin} boxSize={6}/>,
            showConditions: !sessionActive && !eventOver
        },
        {
            isDefaultTab: true,
            short: "event-info",
            long: "Event Info",
            icon: <Icon as={FaRegFileAlt} boxSize={5}/>,
            showConditions: true
        },
        {
            isDefaultTab: false,
            short: "venue-info",
            long: "Venue Info",
            icon: <Icon as={TbLocation} boxSize={5}/>,
            showConditions: !eventOver
        },
        {
            isDefaultTab: false,
            short: "register",
            long: "Register For Event",
            icon: <Icon as={FaUserEdit} boxSize={5}/>,
            showConditions: sessionActive && !eventOver
        },
        {
            isDefaultTab: false,
            short: "ranking",
            long: "Ranking",
            icon: <Icon as={FaListOl} boxSize={5}/>,
            showConditions: rankingAllowed
        },         
        {
            isDefaultTab: false,
            short: "sched-set",
            long: "Schedule Set",
            icon: <Icon as={FaRegCalendarPlus} boxSize={5}/>,
            showConditions: sessionActive && !eventOver
        },
        {
            isDefaultTab: false,
            short: "",
            long: "Clear Sets",
            icon: <Icon as={FaRegCalendarMinus} boxSize={5}/>,
            showConditions: sessionActive && !eventOver && clearAllowed,
            altOnClick: () => { 
                clearSetRequest();
            }
        },       
        {
            isDefaultTab: false,
            short: "calendar",
            long: "Set Calendar",
            icon: <Icon as={FaRegCalendar} boxSize={5}/>,
            showConditions: true
        },
        {
            isDefaultTab: false,
            short: "buy-shirts",
            long: "Purchase Shirts",
            icon: <Icon as={TbShirt} boxSize={5}/>,
            showConditions: sessionActive && !eventOver && !shirtsOver
        },
        {
            isDefaultTab: false,
            short: "song-data",
            long: "Song Data",
            icon: <Icon as={FaMusic} boxSize={5}/>,
            showConditions: songdataAllowed
        },
        {
            isDefaultTab: false,
            short: "",
            long: "Log Out",
            icon: <Icon as={TbLogout} boxSize={6}/>,
            showConditions: sessionActive && !eventOver,
            altOnClick: () => { 
                logoutRequest();
            }
        },        
    ];

    const getNavItems = () => {
        const filteredNavItems = navItems.filter((item) => {
            return item?.showConditions;
        });

        return filteredNavItems;
    }

    const currentNavTabIdx = getNavItems().findIndex((item) => {
        return item?.short === currentPage;
    });

    const navDisabled = (item) => {
        return (
            requestActive ||
            (!schedAllowed && item?.short === "sched-set") ||
            (!regAllowed && item?.short === "register") ||
            (!shirtsAllowed && item?.short === "buy-shirts")
        );
    };

    const NavMenuItems = () => {
        let filteredNavItems = getNavItems();

        const uiNavItems = filteredNavItems?.map((item, idx)=>
            <MenuItem
                icon={currentPage === item?.short ? <MdOutlineKeyboardArrowRight/> : ""}
                isDisabled={navDisabled(item)} 
                onClick={()=>{
                    if (item?.short) {
                        pageOnClick(item?.short);
                    }
                    
                    if (item.hasOwnProperty("altOnClick")) {
                        item.altOnClick();
                    }
                }}
                key={idx}
            >
                {item?.long}
            </MenuItem>
        );

        return uiNavItems;
    }

    const NavTabs = () => {
        let filteredNavItems = getNavItems();

        const uiNavItems = filteredNavItems?.map((item, idx)=>
            <Tab
                isDisabled={navDisabled(item)} 
                onClick={()=>{
                    if (item?.short) {
                        pageOnClick(item?.short);
                    }
                    
                    if (item.hasOwnProperty("altOnClick")) {
                        item.altOnClick();
                    }
                }}
                key={idx}
                _selected={{bg: "gray.800"}}
            >
                {item?.icon ?? <></> }&nbsp;{item?.long}
            </Tab>
        );

        return uiNavItems;
    }    

    try {
        if (isMobile()) {
            return (
                <Flex bgColor={theme.styles?.global?.body?.bg}>
                    <Spacer/>
                    <Menu>
                        <MenuButton bgColor={"blackAlpha.300"} height={12} pl={3} pr={3} margin={2} style={{borderRadius: "15px"}}>
                            <Icon as={RxHamburgerMenu} mt={1} boxSize={7}/>
                        </MenuButton>
                        <MenuList fontSize="1.5rem">
                            <NavMenuItems/>
                        </MenuList>
                    </Menu>
                </Flex>
            )
        } else {
            return (
                <Tabs mt="30px" index={currentNavTabIdx} align='start' variant='enclosed'>
                    <TabList>
                        <NavTabs/>
                    </TabList>
                </Tabs>
            )
        }
    } catch (e) {
        console.error("Navigation render error: ", e);
    }
}